import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  DataTable,
  EmptyState,
  SkeletonLoader,
  StatusChip,
  type DataTableColumn,
} from '@shared/design-system/components';
import {
  getSupportAuditFacets,
  listSupportAudit,
  type SupportAuditEntry,
} from '../../api/supportClient';
import { HeaderDateRange, HeaderFilterSelect } from '../../components/HeaderFilter';

/**
 * The support console's OWN audit trail.
 *
 * This exists as a separate pane, reading a separate table, because
 * support-tier actions must NOT appear in the customer-facing operator
 * SPA's audit screen -- they previously did (real `runbook.run_started`
 * rows were visible to any site operator). The server enforces that
 * structurally via `support_audit_events`; this pane is simply where
 * the trail actually belongs.
 *
 * Every filter is a COLUMN-HEADER control and every filter is applied
 * SERVER-side. The latter is load-bearing rather than stylistic: this
 * endpoint returns only the newest N rows, so filtering a fetched page
 * in the browser would make "show me last month" return an empty table
 * whenever the newest N rows are all from today -- reading as "no
 * support actions occurred" when the truth is "they are older than
 * what was fetched". An audit trail that can silently under-report is
 * worse than no filter at all.
 */

function outcomeKind(outcome: string): 'active' | 'warning' | 'error' | 'offline' {
  if (outcome === 'done' || outcome === 'accepted') return 'active';
  if (outcome === 'failed') return 'error';
  if (outcome === 'denied') return 'warning';
  return 'offline';
}

/** `YYYY-MM-DD` -> epoch ms at LOCAL midnight (start) or the last
 *  millisecond of that day (end). End-of-day matters: picking the same
 *  date in both boxes must mean "that whole day", not a zero-width
 *  range that returns nothing. Local rather than UTC because the date
 *  picker itself shows the user local dates. */
function dayStartMs(day: string): number | undefined {
  if (!day) return undefined;
  const d = new Date(`${day}T00:00:00`);
  return Number.isNaN(d.getTime()) ? undefined : d.getTime();
}

function dayEndMs(day: string): number | undefined {
  if (!day) return undefined;
  const d = new Date(`${day}T23:59:59.999`);
  return Number.isNaN(d.getTime()) ? undefined : d.getTime();
}

const ROW_LIMIT = 500;

export function AuditPane() {
  // `?site=` carries the site the engineer was already looking at
  // (Diagnose writes it) so it survives the trip here. Read on mount
  // only: from then on the column filter owns it.
  const [params] = useSearchParams();
  const [actor, setActor] = useState('');
  const [action, setAction] = useState('');
  const [site, setSite] = useState(params.get('site') ?? '');
  const [outcome, setOutcome] = useState('');
  const [range, setRange] = useState({ from: '', to: '' });

  // Complete, stable option lists for every dropdown. Fetched
  // independently of the filtered rows so choosing one filter never
  // empties the other dropdowns -- see the endpoint's own docstring.
  const facetsQuery = useQuery({
    queryKey: ['support', 'audit', 'facets'],
    queryFn: getSupportAuditFacets,
    staleTime: 60_000,
  });

  const sinceMs = dayStartMs(range.from);
  const untilMs = dayEndMs(range.to);

  const query = useQuery({
    queryKey: ['support', 'audit', actor, action, site, outcome, sinceMs, untilMs],
    queryFn: () =>
      listSupportAudit({
        actor: actor || undefined,
        action_prefix: action || undefined,
        site: site || undefined,
        outcome: outcome || undefined,
        since_ms: sinceMs,
        until_ms: untilMs,
        limit: ROW_LIMIT,
      }),
  });

  const facets = facetsQuery.data;
  const anyFilterActive = Boolean(actor || action || site || outcome || range.from || range.to);

  const actorOptions = useMemo(
    () =>
      (facets?.actors ?? []).map((a) => ({
        value: a.value,
        // One person can hold several ZITADEL subs (verified in real
        // data). Collapsed into one option server-side; the count is
        // shown rather than hidden so the row total is never a
        // surprise.
        label: a.sub_count > 1 ? `${a.label} (${a.sub_count} ids)` : a.label,
      })),
    [facets],
  );

  const columns: DataTableColumn<SupportAuditEntry>[] = [
    {
      id: 'at',
      header: (
        <HeaderDateRange label="When" from={range.from} to={range.to} onChange={setRange} />
      ),
      cell: (r) => new Date(r.at_ms).toLocaleString(),
    },
    {
      id: 'actor',
      header: (
        <HeaderFilterSelect
          label="Actor"
          value={actor}
          options={actorOptions}
          allLabel="All actors"
          onChange={setActor}
        />
      ),
      // Email first: the whole point of capturing it is that a human
      // reading this trail months later can tell who acted. The
      // opaque ZITADEL sub is the fallback, never the headline.
      cell: (r) => (
        <div>
          <div>{r.actor_email ?? r.actor_sub}</div>
          <div style={{ fontSize: 11, color: 'var(--nx-muted)' }}>
            {r.actor_roles.join(', ')}
            {r.actor_email ? ` · ${r.actor_sub}` : ''}
          </div>
        </div>
      ),
    },
    {
      id: 'action',
      header: (
        <HeaderFilterSelect
          label="Action"
          value={action}
          options={(facets?.actions ?? []).map((a) => ({ value: a, label: a }))}
          allLabel="All actions"
          onChange={setAction}
        />
      ),
      cell: (r) => r.action,
    },
    {
      id: 'site',
      header: (
        <HeaderFilterSelect
          label="Site"
          value={site}
          options={(facets?.sites ?? []).map((s) => ({ value: s, label: s }))}
          allLabel="All sites"
          onChange={setSite}
        />
      ),
      // Genuinely nullable: knowledge-base compaction is global.
      cell: (r) => r.site ?? <span style={{ color: 'var(--nx-muted)' }}>global</span>,
    },
    {
      id: 'outcome',
      header: (
        <HeaderFilterSelect
          label="Outcome"
          value={outcome}
          options={(facets?.outcomes ?? []).map((o) => ({ value: o, label: o }))}
          allLabel="All outcomes"
          onChange={setOutcome}
        />
      ),
      cell: (r) => <StatusChip status={outcomeKind(r.outcome)} label={r.outcome} />,
    },
    {
      id: 'reason',
      header: 'Reason',
      cell: (r) => (
        <span title={JSON.stringify(r.target)}>
          {r.reason ?? <span style={{ color: 'var(--nx-muted)' }}>—</span>}
        </span>
      ),
    },
  ];

  const rows = query.data?.entries ?? [];

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>Audit — support actions</h2>
      <p style={{ color: 'var(--nx-muted)', fontSize: 13 }}>
        Every runbook run, abort, outcome recording and knowledge-base change made from this
        console. Not visible to customer operators — their audit screen covers their own commands
        only. Filter using the controls in each column header.
      </p>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 10,
          fontSize: 12,
          color: 'var(--nx-muted)',
        }}
      >
        <span>
          {rows.length} {rows.length === 1 ? 'entry' : 'entries'}
          {/* Never let a truncated result read as a complete one --
              this endpoint is capped, and an audit trail that quietly
              shows a partial answer is the failure mode that matters
              most here. */}
          {rows.length === ROW_LIMIT ? ` (capped at ${ROW_LIMIT} — narrow the range to see more)` : ''}
        </span>
        {anyFilterActive && (
          <button
            type="button"
            onClick={() => {
              setActor('');
              setAction('');
              setSite('');
              setOutcome('');
              setRange({ from: '', to: '' });
            }}
            style={{
              background: 'none',
              border: 0,
              padding: 0,
              color: 'var(--nx-cyan)',
              cursor: 'pointer',
              font: 'inherit',
            }}
          >
            Clear all filters
          </button>
        )}
        {facetsQuery.isError && (
          <span style={{ color: 'var(--nx-error)' }}>
            Filter options unavailable — dropdowns will be empty, but the table below is correct.
          </span>
        )}
      </div>
      {query.isLoading ? (
        <SkeletonLoader shape="table-row" count={6} />
      ) : query.isError ? (
        <EmptyState title="Could not load audit trail" description={String(query.error)} />
      ) : (
        <DataTable
          ariaLabel="Support audit"
          columns={columns}
          rows={rows}
          getRowId={(r) => r.audit_id}
          emptyState={
            <EmptyState
              title="No audit entries"
              description={
                anyFilterActive
                  ? 'No support actions match these filters. Try clearing the date range.'
                  : 'No support actions have been recorded yet.'
              }
            />
          }
        />
      )}
    </div>
  );
}
